// tslint:disable:max-line-length
import { strEnum } from "../../types";
import { TCITenantAppType } from "./tci-application";
import { ITCITenantComponentInput, ITCITenantComponentOutput } from "./tci-component";
import { ITCITenantServiceProvider } from "./tci-tenant";
import { Observable } from "rxjs";

/*************************************************************************************************/
/*                        TCI Marketplace Detail I/O                                             */
/*************************************************************************************************/
/**
 * interface ITCIMarketplaceInput
 * @deprecated
 */
export interface ITCIMarketplaceDetailInput extends ITCITenantComponentInput {
    connectorId: string;
    connector: ITCIMarketplaceConnector;
}

/**
 * interface ITCIMarketplaceOutput
 * @deprecated
 */
export interface ITCIMarketplaceDetailOutput<T> extends ITCITenantComponentOutput<T> {
}

/**
 * interface ITCIMarketplaceEventData
 */
export interface ITCIMarketplaceEventData {
    /**
     * connector selected in marketplace being manipulated
     */
    connector: ITCIMarketplaceConnector;

    /**
     * isWorking indicates if an API request is awaiting response
     * Useful to indicate if a connector is installing or uninstalling
     */
    isWorking: boolean;
}

/**
 * interface ITCIMarketplaceConnectorDetails
 */
// tslint:disable-next-line: class-name
export interface ITCIMarketplaceConnectorDetails {
    /**
     *  installed status
     */
    installed: boolean;
    /**
     * Allows install/uninstall actions
     */
    uninstallable: boolean;
    /**
     * installation url to be invoked e.g. scribe installation service API endpoint
     */
    install_url: string;
    /**
     * uninstall url to be invoked e.g. scribe installation service API endpoint
     */
    uninstall_url: string;
    /**
     * Distributor name
     */
    dist_by: string;
    /**
     * Distributor creation date UTF timestamp
     */
    dist_dt: number;

    /**
     * Connector upload date UTF timestamp
     */
    upload_dt: number;
    /**
     * certified status
     */
    certified: boolean;
    /**
     * certified date UTF timestamp
     */
    cert_dt: Date;
    /**
     * Commercial licensing status
     */
    commercial_lic: boolean;
    /**
     * Commercial licensing url
     */
    commercial_lic_url: string;
    /**
     * Paid or free indicator
     */
    isFree: boolean;

    /**
     * Miscellaneous info for Scribe
     */
    isISSource?: boolean;
    isISTarget?: boolean;
    isMSSource?: boolean;
    isMSTarget?: boolean;
    isRSSource?: boolean;
    link?: string;
    supportCloudAgents?: boolean;
    /**
     * Addition info required for the UI Connector Tile.
     */
    version?: string;
    lastModifiedDate?: string;
    author?: string;
}

/**
 * Common TCI/Scribe Connector Object
 */
export interface ITCIMarketplaceConnector {
    /**
     * unique identifier for the connector (not displayed)
     */
    id: string;
    /**
     * Connector long name displayed on the card
     */
    name: string;
    /**
     * Connector description
     */
    description: string;
    /**
     * Specifies the small icon URL path
     */
    smallIcon: string;
    /**
     * Specifies the small icon URL path
     */
    largeIcon: string;
    /**
     * TCIConnectorTechType icon i.e. scribe/flogo
     */
    techIcon: string;
    /**
     *  Distributor entity logo i.e. TIBCO / Third party
     */
    distIcon: string;
    /**
     * Connector Tech Type
     */
    tenantType: TCITenantAppType;
    /**
     * Connector details
     */
    details: ITCIMarketplaceConnectorDetails;
}

/**
 * TCI Tenant Marketplace Connector supported technologies
 */
export class TCIMarketplaceSupportedTechs {
    public static cloudAgents = new TCIMarketplaceSupportedTechs("supportCloudAgents", "Works with Cloud Agents.");
    public static integration = new TCIMarketplaceSupportedTechs("isISSource", "Works with  Integration Services (IS).");
    public static migration = new TCIMarketplaceSupportedTechs("isMSSource", "Works with  Migration Services (MS).");
    public static replication = new TCIMarketplaceSupportedTechs("isRSSource", "Works with  Replication Services (RS).");

    public static getAll(): TCIMarketplaceSupportedTechs[] {
        return [
            TCIMarketplaceSupportedTechs.cloudAgents,
            TCIMarketplaceSupportedTechs.integration,
            TCIMarketplaceSupportedTechs.migration,
            TCIMarketplaceSupportedTechs.replication
        ];
    }

    constructor(public name: string, public label: string) { }
}

export interface ITCIMarketplaceGetConnectorsRequestError {
    type: TCITenantAppType;
    status: number;
}

export interface ITCIMarketplaceGetConnectorsResponse {
    connectors: ITCIMarketplaceConnector[];
    errors: ITCIMarketplaceGetConnectorsRequestError[];
}

export interface ITCIMarketplaceConnectorInstallRequest {
    connector: ITCIMarketplaceConnector;
}

export interface ITCIMarketplaceConnectorInstallResponse {
    /**
     * Application Id
     */
    connectorId: string;
    /**
     * Application Response status
     */
    success: boolean;
    /**
     * Application Response message
     */
    msg?: string;
}

export interface ITCIMarketplaceConnectorDeleteRequest {
    connectorId: string;
    tenantType: TCITenantAppType;
}

export interface ITCIMarketplaceConnectorDeleteResponse {
    /**
     * Application Id
     */
    connectorId: string;
    /**
     * Application Response status
     */
    success: boolean;
    /**
     * Application Response message
     */
    msg?: string;
}

export interface ITCIMarketplaceIconReqResp {
    artifactId: string;
    smallIcon: string;
    largeIcon: string;
}

/**
 * @deprecated
 */
export interface ITCIMarketplaceProvider {
    name: string;
    displayName: string;
    count?: number;
    selected?: boolean;
    error?: string;
}

/**
 * TCI_MARKETPLACE_ACTION_TYPE
 * @type {{[K in string]: K}}
 */
export const TCI_MARKETPLACE_ACTION_TYPE = strEnum([
    "MARKETPLACE_INSTALL",
    "MARKETPLACE_UNINSTALL",
    "MARKETPLACE_IS_ENABLED",
    "MARKETPLACE_SUPPORTED_TECHNOLOGIES",
    "MARKETPLACE_REQUEST_CONNECTOR"
]);
export type TCI_MARKETPLACE_ACTION_TYPE = keyof typeof TCI_MARKETPLACE_ACTION_TYPE;

/**
 * interface ITCIMarketplaceConnectorRequest
 */
export interface ITCIMarketplaceConnectorRequest extends ITCIMarketplaceConnectorInstallRequest {
    /**
     * connector Id
     * @deprecated
     */
    connectorId?: string;
    /**
     * tenant type
     * @deprecated
     */
    tenantType?: TCITenantAppType;
    /**
     * action type
     */
    actionType: TCI_MARKETPLACE_ACTION_TYPE;
    /**
     * Connector instance to be evaluated
     */
    connector: ITCIMarketplaceConnector;
}

/**
 * interface ITCIMarketplaceConnectorResponse
 */
// tslint:disable-next-line:no-empty-interface
export interface ITCIMarketplaceConnectorResponse extends ITCIMarketplaceConnectorInstallResponse {
    /**
     * Connector instance
     */
    connector: ITCIMarketplaceConnector;
    /**
     *  Response status
     */
    success: boolean;
    /**
     *  Response message
     */
    msg?: string;
}

/**
 *  Marketplace Provider
 */
export interface ITCIMarketplaceServiceProvider extends ITCITenantServiceProvider {
    /**
     * Get list of market place connectors provided by the tenant
     * This for use by Flogo tenant who will provide the connectors from a UI cache
     */
    getConnectors(): Observable<ITCIMarketplaceConnector[]>;

    /**
     * Get a single connector data
     * @param input
     */
    getConnectorById(input: ITCIMarketplaceConnectorRequest): Observable<ITCIMarketplaceConnector>;

    /**
     * Tenant providers decides if the connector can be installed
     * @param input
     */
    canInstallConnector(input: ITCIMarketplaceConnectorRequest): Observable<boolean>;

    /**
     * install connector
     * @param input
     */
    installConnector(input: ITCIMarketplaceConnectorRequest): Observable<ITCIMarketplaceConnectorResponse>;

    /**
     * Tenant providers decides if the connector can be uninstalled
     * @param input
     */
    canUninstallConnector(input: ITCIMarketplaceConnectorRequest): Observable<boolean>;

    /**
     * install connector
     * @param input
     */
    uninstallConnector(input: ITCIMarketplaceConnectorRequest): Observable<ITCIMarketplaceConnectorResponse>;

    /**
     * Check if the connector is enabled
     * @param input
     */
    isConnectorEnabled(input: ITCIMarketplaceConnectorRequest): Observable<boolean>;

    /**
     * Return the catalog of the connector supported technologies
     * @param input
     */
    getSuportedTechs(input: ITCIMarketplaceConnectorRequest): Observable<TCIMarketplaceSupportedTechs[]>;
}
